<script setup>
import { ref, onMounted, computed } from "vue";
import axios from "axios";
import BackgroundSection from "./BackgroundSection.vue";
import content from "../content.js";

const testimonialsContent = content.testimonials;
const testimonials = ref([]);
const loading = ref(true);

// Fetch testimonials from API
const fetchTestimonials = async () => {
    try {
        loading.value = true;
        const response = await axios.get("/api/testimonials");

        if (response.data.success && response.data.data.length > 0) {
            // Get all testimonials and shuffle them
            const allTestimonials = response.data.data;
            const shuffled = allTestimonials.sort(() => 0.5 - Math.random());

            // Get random 5 testimonials (or less if not enough)
            testimonials.value = shuffled.slice(0, 5);
        }
    } catch (err) {
        console.error("Error fetching testimonials:", err);
    } finally {
        loading.value = false;
    }
};

// Navigate to testimonials page
const viewAllTestimonials = () => {
    window.location.href = "/testimonials";
};

// Computed property for display message
const hasTestimonials = computed(() => testimonials.value.length > 0);

// Generate random rotation angle for each photo
const getRotationClass = (index) => {
    const rotations = [
        "rotate-3",
        "-rotate-2",
        "rotate-6",
        "-rotate-3",
        "rotate-2",
        "-rotate-6",
        "rotate-1",
        "-rotate-1",
    ];
    return rotations[index % rotations.length];
};

onMounted(() => {
    fetchTestimonials();
});
</script>

<template>
    <BackgroundSection
        section-id="testimonials"
        :video="testimonialsContent.video"
        :image="testimonialsContent.image"
        overlay-class="bg-black/60"
    >
        <div class="max-w-7xl mx-auto px-6 py-16">
            <!-- Header -->
            <div class="text-center mb-12" data-aos="fade-up">
                <h2
                    class="text-3xl sm:text-4xl md:text-5xl font-bold text-shadow mb-4"
                >
                    {{ testimonialsContent.title }}
                </h2>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="text-center py-12">
                <div
                    class="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"
                ></div>
                <p class="text-white mt-4 text-shadow">
                    Loading testimonials...
                </p>
            </div>

            <!-- Empty State / Default Message -->
            <div v-else-if="!hasTestimonials" class="text-center py-16">
                <div
                    class="bg-white/10 backdrop-blur-md rounded-2xl shadow-xl p-12 max-w-2xl mx-auto"
                    data-aos="fade-up"
                >
                    <svg
                        class="w-20 h-20 mx-auto mb-6 text-green-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                        ></path>
                    </svg>
                    <h3 class="text-3xl font-bold text-white mb-3 text-shadow">
                        Testimonials Coming Soon!
                    </h3>
                    <p class="text-xl text-gray-200 text-shadow">
                        We're gathering feedback from our valued clients. Check
                        back soon to see what they're saying about us!
                    </p>
                </div>
            </div>

            <!-- Testimonials Grid - 5 photos: 3 in first row, 2 in second row -->
            <div v-else>
                <!-- First Row - 3 photos -->
                <div
                    class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-8"
                >
                    <div
                        v-for="(testimonial, index) in testimonials.slice(0, 3)"
                        :key="testimonial.id"
                        class="flex justify-center items-center"
                        data-aos="zoom-in"
                        :data-aos-delay="index * 100"
                    >
                        <div
                            class="relative group perspective-1000"
                            :class="getRotationClass(index)"
                        >
                            <!-- Photo Frame -->
                            <div
                                class="bg-white p-3 rounded-lg shadow-2xl hover:shadow-green-500/50 transition-all duration-300 transform hover:scale-105 hover:rotate-0"
                            >
                                <img
                                    :src="testimonial.url"
                                    :alt="testimonial.file_name"
                                    loading="lazy"
                                    class="w-full aspect-square object-cover rounded"
                                />
                            </div>

                            <!-- Subtle Shadow Effect -->
                            <div
                                class="absolute -inset-1 bg-gradient-to-br from-green-400/20 to-blue-400/20 rounded-lg -z-10 blur-md group-hover:blur-xl transition-all"
                            ></div>
                        </div>
                    </div>
                </div>

                <!-- Second Row - 2 photos centered -->
                <div
                    v-if="testimonials.length > 3"
                    class="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl mx-auto mb-12"
                >
                    <div
                        v-for="(testimonial, index) in testimonials.slice(3, 5)"
                        :key="testimonial.id"
                        class="flex justify-center items-center"
                        data-aos="zoom-in"
                        :data-aos-delay="(index + 3) * 100"
                    >
                        <div
                            class="relative group perspective-1000"
                            :class="getRotationClass(index + 3)"
                        >
                            <!-- Photo Frame -->
                            <div
                                class="bg-white p-3 rounded-lg shadow-2xl hover:shadow-green-500/50 transition-all duration-300 transform hover:scale-105 hover:rotate-0"
                            >
                                <img
                                    :src="testimonial.url"
                                    :alt="testimonial.file_name"
                                    loading="lazy"
                                    class="w-full aspect-square object-cover rounded"
                                />
                            </div>

                            <!-- Subtle Shadow Effect -->
                            <div
                                class="absolute -inset-1 bg-gradient-to-br from-green-400/20 to-blue-400/20 rounded-lg -z-10 blur-md group-hover:blur-xl transition-all"
                            ></div>
                        </div>
                    </div>
                </div>

                <!-- Check Out More Button -->
                <div
                    class="text-center mt-12"
                    data-aos="fade-up"
                    data-aos-delay="500"
                >
                    <button
                        @click="viewAllTestimonials"
                        class="px-8 py-4 bg-white/20 backdrop-blur-md border border-white/30 rounded-full font-semibold text-lg text-white transition-all duration-300 transform hover:scale-105 hover:bg-white/30 shadow-lg hover:shadow-xl"
                    >
                        Check Out More Testimonials
                    </button>
                </div>
            </div>
        </div>
    </BackgroundSection>
</template>

<style scoped>
.perspective-1000 {
    perspective: 1000px;
}
</style>
